import type { ApiResult } from '../types';
import type {
  ApiApplicableAreaRow,
  ApiCategoryRow,
  ApiChecklistDocumentRow,
  ApiCourtRow,
  ApiLookupRow,
  ApiSubCategoryRow,
  CaseRequestCategoryTree,
  CriminalStepsResponse,
  CaseFileDownloadResponse,
  CaseRequestDocumentsResponse,
  CriminalCoverNoteResponse,
  CriminalCoverNoteSuspect,
  UploadAdditionalDocumentResponse,
  UploadCaseFileResponse,
} from '../types/criminalApi';
import { base64ToBlob } from '../utils/base64Blob';
import type { CriminalWizardDraft } from '../types/criminalCaseRequest';
import { createEmptyCriminalDraft } from './criminalCaseMapper';
import type { DocumentChecklistItem, LookupItem, SubFileCategory } from '../mocks/criminalMasterData';

export type { LookupItem };
import {
  CURRENT_CUSTODY_STATUSES,
  FILE_CATEGORY_CRIMINAL,
  getDocumentChecklistForFileCategory,
  URGENCY_LEVELS,
} from '../mocks/criminalMasterData';
import { caseApiPdfRequest, caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import { cachedCriminalFetch } from './criminalLookupCache';
import { isDocumentTypeUuid } from '../utils/documentTypeId';
import { normalizeDocumentFileFields } from '../utils/fileDisplayName';
import { registerWizardCategoryIdsFromLookups } from '../utils/wizardCategoryRoute';
import {
  buildSaveCriminalPayload,
  caseRequestIdFromResponse,
  mapCriminalStepsToDraft,
  parseApplicableAreaLabels,
  referenceNoFromResponse,
  resolveCriminalDraftResumeStep,
  type CriminalWizardStep,
} from './criminalCaseMapper';

function lookupRowId(row: ApiLookupRow): string {
  return (row.id ?? row.code ?? '').trim();
}

function normalizeLookupRows(data: unknown): ApiLookupRow[] {
  if (!Array.isArray(data)) return [];
  const rows: ApiLookupRow[] = [];
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const id =
      typeof r.id === 'string'
        ? r.id.trim()
        : typeof r.code === 'string'
          ? r.code.trim()
          : '';
    if (!name || !id) continue;
    rows.push({
      id,
      name,
      code: typeof r.code === 'string' ? r.code : undefined,
      description: typeof r.description === 'string' ? r.description : undefined,
      active: typeof r.active === 'boolean' ? r.active : undefined,
    });
  }
  return rows;
}

function toLookup(row: ApiLookupRow): LookupItem {
  return { id: lookupRowId(row), name: row.name, code: row.code };
}

function toSubCategoryLookup(row: ApiLookupRow): LookupItem {
  return { id: lookupRowId(row), name: row.name.trim(), code: row.code };
}

function toLookupList(rows: ApiLookupRow[] | null | undefined): LookupItem[] {
  const normalized = Array.isArray(rows) ? rows : normalizeLookupRows(rows);
  return normalized.filter((r) => r.name && lookupRowId(r) && r.active !== false).map(toLookup);
}

function normalizeCourtRows(data: unknown): ApiCourtRow[] {
  if (!Array.isArray(data)) return [];
  const rows: ApiCourtRow[] = [];
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const id =
      typeof r.id === 'string'
        ? r.id.trim()
        : typeof r.code === 'string'
          ? r.code.trim()
          : '';
    if (!name || !id) continue;
    rows.push({
      id,
      name,
      code: typeof r.code === 'string' ? r.code : undefined,
      active: typeof r.active === 'boolean' ? r.active : undefined,
      courtType: typeof r.courtType === 'string' ? r.courtType : undefined,
      divisionId: typeof r.divisionId === 'string' ? r.divisionId : undefined,
    });
  }
  return rows;
}

function orderIndexOf(row: { order_index?: number; orderIndex?: number }): number {
  return row.order_index ?? row.orderIndex ?? 0;
}

function isVisibleInInternalApp(row: ApiCategoryRow): boolean {
  const flag = row.show_in_internal_app ?? row.showInInternalApp;
  if (flag === undefined) return true;
  return flag === true;
}

function sortByOrderIndex<T extends { order_index?: number; orderIndex?: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => orderIndexOf(a) - orderIndexOf(b));
}

function parentIdOf(row: ApiCategoryRow): string | undefined {
  return row.parentId ?? row.parent_id ?? row.categoryId ?? row.category_id;
}

function isCriminalCategory(row: ApiCategoryRow): boolean {
  const n = row.name?.toLowerCase() ?? '';
  const c = row.code?.toLowerCase() ?? '';
  return n.includes('criminal') || c.includes('criminal') || c === 'cr';
}

function parseCategoryTree(rows: ApiCategoryRow[]): CaseRequestCategoryTree {
  const criminal =
    rows.find(isCriminalCategory) ??
    rows.find((r) => r.name?.toLowerCase() === FILE_CATEGORY_CRIMINAL.toLowerCase());

  if (!criminal) {
    return {
      fileCategoryId: '',
      fileCategoryName: FILE_CATEGORY_CRIMINAL,
      subCategories: [],
    };
  }

  const children = rows
    .filter((r) => parentIdOf(r) === criminal.id)
    .map((r) => ({ id: r.id, name: r.name, code: r.code }));

  if (children.length) {
    return { fileCategoryId: criminal.id, fileCategoryName: criminal.name, subCategories: children };
  }

  const subs = rows
    .filter((r) => r.id !== criminal.id && !isCriminalCategory(r))
    .map((r) => ({ id: r.id, name: r.name, code: r.code }));

  return {
    fileCategoryId: criminal.id,
    fileCategoryName: criminal.name,
    subCategories: subs,
  };
}

async function fetchLookupList(path: string): Promise<ApiResult<LookupItem[]>> {
  const res = await caseApiRequest<unknown>(path);
  if (!res.ok) return { success: false, message: res.message };
  const items = toLookupList(normalizeLookupRows(res.data));
  return { success: true, data: items };
}

function cacheLookupResult(result: ApiResult<LookupItem[]>): boolean {
  return result.success && result.data.length > 0;
}

async function fetchLookupCached(cacheKey: string, path: string): Promise<ApiResult<LookupItem[]>> {
  return cachedCriminalFetch(cacheKey, () => fetchLookupList(path), {
    cacheWhen: cacheLookupResult,
  });
}

/** CMS-MCRC-1 — active file categories */
export async function getFileCategories(): Promise<ApiResult<LookupItem[]>> {
  const result = await cachedCriminalFetch('file-categories', async (): Promise<ApiResult<LookupItem[]>> => {
    const res = await caseApiRequest<ApiCategoryRow[]>('/case-request-categories');
    if (!res.ok) return { success: false, message: res.message };
    const rows = sortByOrderIndex(res.data ?? []).filter(
      (r) => r.active !== false && isVisibleInInternalApp(r),
    );
    const data = rows.map(toLookup);
    return { success: true, data };
  });
  if (result.success) registerWizardCategoryIdsFromLookups(result.data);
  return result;
}

/** CMS-MCRSC-1 — active sub categories for a file category */
export async function getSubFileCategories(
  categoryId: string,
  options?: { useCache?: boolean },
): Promise<ApiResult<LookupItem[]>> {
  if (!categoryId) return { success: true, data: [] };
  const useCache = options?.useCache ?? true;

  const fetcher = async (): Promise<ApiResult<LookupItem[]>> => {
    const res = await caseApiRequest<ApiSubCategoryRow[]>(
      `/case-request-sub-categories?category_id=${encodeURIComponent(categoryId)}`,
    );
    if (!res.ok) return { success: false as const, message: res.message };
    const rows = sortByOrderIndex(normalizeLookupRows(res.data) as ApiSubCategoryRow[]).filter(
      (r) => r.active !== false,
    );
    return { success: true as const, data: rows.map(toSubCategoryLookup) };
  };

  return useCache ? cachedCriminalFetch(`sub-categories:${categoryId}`, fetcher) : fetcher();
}

/** CMS-MCRSCAA-1 — applicable areas for a sub file category */
export async function getSubCategoryApplicableAreas(
  subCategoryId: string,
  options?: { useCache?: boolean },
): Promise<ApiResult<LookupItem[]>> {
  if (!subCategoryId) return { success: true, data: [] };
  const useCache = options?.useCache ?? true;

  const fetcher = async (): Promise<ApiResult<LookupItem[]>> => {
    const res = await caseApiRequest<ApiApplicableAreaRow[]>(
      `/case-request-sub-category-applicable-areas?sub_category_id=${encodeURIComponent(subCategoryId)}`,
    );
    if (!res.ok) return { success: false as const, message: res.message };
    const rows = sortByOrderIndex(res.data ?? []).filter((r) => r.active !== false);
    return { success: true as const, data: rows.map(toLookup) };
  };

  return useCache ? cachedCriminalFetch(`applicable-areas:${subCategoryId}`, fetcher) : fetcher();
}

/** @deprecated Use getFileCategories + getSubFileCategories */
export async function getCaseRequestCategories(): Promise<
  ApiResult<{ fileCategory: string; fileCategoryId: string; subCategories: SubFileCategory[] }>
> {
  const res = await caseApiRequest<ApiCategoryRow[]>('/case-request-categories');
  if (!res.ok) {
    return {
      success: true,
      data: {
        fileCategory: FILE_CATEGORY_CRIMINAL,
        fileCategoryId: '',
        subCategories: [],
      },
    };
  }

  const tree = parseCategoryTree((res.data ?? []).filter(isVisibleInInternalApp));
  const subCategories: SubFileCategory[] = tree.subCategories.map((s) => ({
    id: s.id,
    name: s.name,
    applicableAreas: [],
  }));

  return {
    success: true,
    data: {
      fileCategory: tree.fileCategoryName,
      fileCategoryId: tree.fileCategoryId,
      subCategories,
    },
  };
}

/** CMS-MP-1 */
export async function getProvinces(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('province', '/province');
}

/** CMS-MD-1 */
export async function getDistricts(provinceId: string): Promise<ApiResult<LookupItem[]>> {
  if (!provinceId) return { success: true, data: [] };
  return cachedCriminalFetch(`districts:${provinceId}`, async () => {
    const res = await caseApiRequest<unknown>(`/districts?provinceId=${encodeURIComponent(provinceId)}`);
    if (!res.ok) return { success: false, message: res.message };
    return { success: true, data: toLookupList(normalizeLookupRows(res.data)) };
  });
}

/** CMS-MC-1 — GET /v1/case-requests/courts (courtType required; districtId and provinceId optional) */
export async function getCourts(
  courtType: string,
  districtId?: string,
  provinceId?: string,
): Promise<ApiResult<LookupItem[]>> {
  const type = courtType.trim();
  if (!type) return { success: true, data: [] };

  const district = districtId?.trim() ?? '';
  const province = provinceId?.trim() ?? '';
  const cacheKey = `courts:${type}:${district}:${province}`;
  return cachedCriminalFetch(cacheKey, async () => {
    const qs = new URLSearchParams({ courtType: type });
    if (district) qs.set('districtId', district);
    if (province) qs.set('provinceId', province);
    const res = await caseApiRequest<unknown>(`/courts?${qs}`);
    if (!res.ok) return { success: false, message: res.message };

    const rows = normalizeCourtRows(res.data).filter((c) => c.active !== false);
    return { success: true, data: rows.map(toLookup) };
  });
}

/** CMS-MPS-1 — police stations filtered by district */
export async function getPoliceStations(districtId?: string): Promise<ApiResult<LookupItem[]>> {
  if (!districtId) return { success: true, data: [] };
  return cachedCriminalFetch(`police-stations:${districtId}`, async () => {
    const res = await caseApiRequest<unknown>(`/police-stations?districtId=${encodeURIComponent(districtId)}`);
    if (!res.ok) return { success: false, message: res.message };
    return { success: true, data: toLookupList(normalizeLookupRows(res.data)) };
  });
}

/** CMS-UL-1 (fallback mock if endpoint unavailable) */
export async function getUrgencyLevels(): Promise<ApiResult<LookupItem[]>> {
  return cachedCriminalFetch(
    'urgency-levels',
    async () => {
      const res = await fetchLookupList('/urgency-level');
      if (res.success && res.data.length) return res;
      return { success: true, data: URGENCY_LEVELS };
    },
    { cacheWhen: (r) => r.success && r.data.length > 0 },
  );
}

/** CMS-MSS-1 */
export async function getSuspectStatuses(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('suspect-statuses', '/suspect-status');
}

function parseActiveLookupRows(data: unknown): LookupItem[] {
  if (!Array.isArray(data)) return [];
  const rows: LookupItem[] = [];
  for (const entry of data) {
    if (!entry || typeof entry !== 'object') continue;
    const r = entry as Record<string, unknown>;
    if (r.active === false) continue;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const id =
      typeof r.id === 'string'
        ? r.id.trim()
        : typeof r.id === 'number' && Number.isFinite(r.id)
          ? String(r.id)
          : typeof r.code === 'string'
            ? r.code.trim()
            : '';
    if (!name || !id) continue;
    rows.push({
      id,
      name,
      code: typeof r.code === 'string' ? r.code : undefined,
    });
  }
  return rows;
}

/** CMS-MCC-1 — active current custody statuses */
export async function getCurrentCustodyStatuses(): Promise<ApiResult<LookupItem[]>> {
  return cachedCriminalFetch(
    'current-custody-statuses',
    async () => {
      const res = await caseApiRequest<unknown>('/current-custody-statuses');
      if (res.ok) {
        const items = parseActiveLookupRows(res.data);
        if (items.length) return { success: true, data: items };
      }
      return { success: true, data: CURRENT_CUSTODY_STATUSES };
    },
    {
      cacheWhen: (result) =>
        Boolean(result.success && result.data.length && result.data.some((row) => row.id.includes('-'))),
    },
  );
}

/** CMS-MDM-1 */
export async function getDeliveryMethods(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('delivery-methods', '/physical-file-delivery-methods');
}

/** CMS case sources — GET /v1/case-requests/case-source */
export async function getCaseSources(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('case-source', '/case-source');
}

/** CMS-MCI-1 — active corporate institutes (mst_corporate_institute). */
export async function getCorporateInstitutes(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('corporate-institutes', '/corporate-institutes');
}

/** Gender master — GET /v1/case-requests/genders */
export async function getGenders(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('genders', '/genders');
}


/** Respondent types — GET /v1/case-requests/respondent-types */
export async function getRespondentTypes(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('respondent-types', '/respondent-types');
}
/** CMS-ML-1 — GET /v1/case-requests/languages */
export async function getLanguages(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('languages', '/languages');
}

/** CMS-MMOA-1 — GET /v1/case-requests/methods-of-access */
export async function getMethodsOfAccess(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('methods-of-access', '/methods-of-access');
}

/** CMS-MNA-1 — GET /v1/case-requests/nationalities */
export async function getNationalities(): Promise<ApiResult<LookupItem[]>> {
  return fetchLookupCached('nationalities', '/nationalities');

}

function checklistStrField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function checklistMandatory(raw: Record<string, unknown>): boolean {
  if (raw.mandatory === true || raw.is_mandatory === true || raw.isMandatory === true) return true;
  if (raw.optional === true || raw.is_optional === true || raw.isOptional === true) return false;
  if (raw.optional === false || raw.is_optional === false || raw.isOptional === false) return true;
  return false;
}

/** CMS-MCRCD-1 rows may use snake_case; document_type_id is the save payload id (not checklist row id or code). */
function normalizeChecklistRows(data: unknown): DocumentChecklistItem[] {
  if (!Array.isArray(data)) return [];
  const items: DocumentChecklistItem[] = [];
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    if (r.active === false) continue;

    const id = checklistStrField(r, 'id');
    const documentTypeId = checklistStrField(r, 'document_type_id', 'documentTypeId');
    if (!id || !isDocumentTypeUuid(documentTypeId)) continue;

    const name =
      checklistStrField(r, 'document_type_name', 'documentTypeName') ||
      checklistStrField(r, 'document_type_code', 'documentTypeCode') ||
      'Document';
    const documentTypeCode = checklistStrField(r, 'document_type_code', 'documentTypeCode') || undefined;

    items.push({
      id,
      documentTypeId,
      name,
      documentTypeCode,
      mandatory: checklistMandatory(r),
    });
  }
  return items;
}

function mockDocumentChecklistEnabled(): boolean {
  return (import.meta.env.VITE_USE_MOCK_API ?? '').trim() === 'true';
}

function mockDocumentChecklistFallback(fileCategoryId: string): DocumentChecklistItem[] {
  return mockDocumentChecklistEnabled() ? getDocumentChecklistForFileCategory(fileCategoryId) : [];
}

/** CMS-MCRCD-1 — active checklist documents for file category (categoryId = file category uuid) */
export async function getDocumentChecklist(
  fileCategoryId: string,
  subCategoryId?: string,
): Promise<ApiResult<DocumentChecklistItem[]>> {
  if (!fileCategoryId) return { success: true, data: [] };
  const subId = subCategoryId?.trim() ?? '';
  const cacheKey = subId
    ? `document-checklist:${fileCategoryId}:${subId}`
    : `document-checklist:${fileCategoryId}`;
  return cachedCriminalFetch(cacheKey, async () => {
    const qs = new URLSearchParams({ categoryId: fileCategoryId });
    if (subId) qs.set('subCategoryId', subId);
    const res = await caseApiRequest<ApiChecklistDocumentRow[] | ApiChecklistDocumentRow>(
      `/case-request-checklist-documents?${qs.toString()}`,
    );
    if (!res.ok) {
      const fallback = mockDocumentChecklistFallback(fileCategoryId);
      return fallback.length
        ? { success: true, data: fallback }
        : { success: false, message: res.message };
    }

    const raw = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
    const items = normalizeChecklistRows(raw);
    if (items.length) return { success: true, data: items };

    const fallback = mockDocumentChecklistFallback(fileCategoryId);
    return fallback.length ? { success: true, data: fallback } : { success: true, data: [] };
  });
}

export type CriminalRequestViewData = {
  draft: CriminalWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: CriminalStepsResponse['step_1'];
  step2?: CriminalStepsResponse['step_2'];
  step3?: CriminalStepsResponse['step_3'];
  step4?: CriminalStepsResponse['step_4'];
};

/** CMS-CRC-1 — wizard resume: steps only (drafts may not have a cover note yet). */
export async function getCriminalWizardDraft(
  caseRequestId: string,
): Promise<ApiResult<CriminalWizardDraft | null>> {
  const id = caseRequestId.trim();
  if (!id) return { success: true, data: null };

  const res = await caseApiRequest<CriminalStepsResponse>(`/criminal/steps/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };

  const draft = mapCriminalStepsToDraft(res.data, {
    ...createEmptyCriminalDraft(),
    draftRequestId: caseRequestIdFromResponse(res.data) || id,
  });

  return { success: true, data: draft };
}

/** CMS-CRC-1 */
export async function getCriminalRequestDetails(caseRequestId: string): Promise<ApiResult<CriminalWizardDraft | null>> {
  const view = await getCriminalRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

/** CMS-CRC-1 — continue draft: resolve wizard step + draft from one steps GET. */
export async function getCriminalDraftResume(
  caseRequestId: string,
): Promise<ApiResult<{ step: CriminalWizardStep; requestId: string; draft: CriminalWizardDraft }>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<CriminalStepsResponse>(`/criminal/steps/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || id;
  const draft = mapCriminalStepsToDraft(res.data, {
    ...createEmptyCriminalDraft(),
    draftRequestId: requestId,
  });

  return {
    success: true,
    data: {
      step: resolveCriminalDraftResumeStep(res.data),
      requestId,
      draft,
    },
  };
}

/** CMS-CRC-1 — which wizard step to open when continuing a saved draft */
export async function getCriminalDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: CriminalWizardStep; requestId: string }>> {
  const res = await getCriminalDraftResume(caseRequestId);
  if (!res.success) return { success: false, message: res.message };
  return { success: true, data: { step: res.data.step, requestId: res.data.requestId } };
}

/** CMS-CRC-1 — full view payload for View Case Request screen */
const criminalViewInflight = new Map<string, Promise<ApiResult<CriminalRequestViewData | null>>>();

export function invalidateCriminalRequestViewCache(caseRequestId?: string): void {
  if (caseRequestId?.trim()) {
    const id = caseRequestId.trim();
    criminalViewInflight.delete(id);
    criminalViewInflight.delete(`${id}|original`);
    criminalViewInflight.delete(`${id}|latest`);
  } else {
    criminalViewInflight.clear();
  }
}

async function fetchCriminalRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<CriminalRequestViewData | null>> {
  const res = await caseApiRequest<CriminalStepsResponse>(
    caseRequestStepsPath(`/criminal/steps/${encodeURIComponent(caseRequestId)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  let draft = mapCriminalStepsToDraft(res.data, {
    ...createEmptyCriminalDraft(),
    draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
  });

  const coverRes = await getCriminalCoverNote(caseRequestId);
  const submittedAt =
    (coverRes.success ? coverRes.data.requestSubmittedAt : '').trim() ||
    (res.data.created_at ?? res.data.updated_at ?? '').trim();

  if (coverRes.success) {
    const fromSuspects = coverRes.data.suspects.filter((s) => s.suspectName.trim());
    if (fromSuspects.length && draft.accused.every((a) => !a.name.trim())) {
      draft = {
        ...draft,
        accused: fromSuspects.map((s) => ({
          id: crypto.randomUUID(),
          name: s.suspectName,
          status: '',
          statusOther: s.suspectStatus,
        })),
      };
    } else {
      const accusedName = coverRes.data.accusedOrSuspectName.trim();
      if (accusedName && draft.accused.every((a) => !a.name.trim())) {
        const accused = draft.accused.length
          ? draft.accused.map((a, index) => (index === 0 ? { ...a, name: accusedName } : a))
          : [{ id: crypto.randomUUID(), name: accusedName, status: '', statusOther: '' }];
        draft = { ...draft, accused };
      }
    }
  }

  if (!draft.applicableAreas.length) {
    if (coverRes.success) {
      const fromCover = parseApplicableAreaLabels(coverRes.data.applicableAreas);
      if (fromCover.length) {
        draft = { ...draft, applicableAreas: fromCover };
      }
    }
  }

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const raw = res.data;
  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
    },
  };
}

export async function getCriminalRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<CriminalRequestViewData | null>> {
  const id = caseRequestId.trim();
  if (!id) return { success: true, data: null };

  const cacheKey = `${id}|${options?.view === 'original' ? 'original' : 'latest'}`;
  const inflight = criminalViewInflight.get(cacheKey);
  if (inflight) return inflight;

  const promise = fetchCriminalRequestViewData(id, options).finally(() => {
    if (criminalViewInflight.get(cacheKey) === promise) {
      criminalViewInflight.delete(cacheKey);
    }
  });
  criminalViewInflight.set(cacheKey, promise);
  return promise;
}

export type SaveCriminalResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS-CRC-2 */
export async function saveCriminalRequest(
  draft: CriminalWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: CriminalWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveCriminalResult>> {
  const body = buildSaveCriminalPayload(draft, {
    isDraft: options.isDraft,
    currentStep: options.currentStep,
    allSteps: options.allSteps,
    confirmedCorrect: options.confirmedCorrect,
    isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
  });

  const res = await caseApiRequest<CriminalStepsResponse>('/criminal/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;
  invalidateCriminalRequestViewCache(requestId);

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: referenceNoFromResponse(res.data),
    },
  };
}

function strField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

export function parseCriminalCoverNoteSuspects(raw: unknown): CriminalCoverNoteSuspect[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item): CriminalCoverNoteSuspect | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const suspectName = strField(row, 'suspectName', 'suspect_name', 'name');
      const suspectStatus = strField(
        row,
        'suspectStatus',
        'suspect_status',
        'suspect_status_name',
        'status_name',
        'status',
      );
      if (!suspectName && !suspectStatus) return null;
      return { suspectName, suspectStatus };
    })
    .filter((item): item is CriminalCoverNoteSuspect => item !== null);
}

export function normalizeCriminalCoverNoteResponse(raw: unknown): CriminalCoverNoteResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const docs = r.documents;
  const documentList = Array.isArray(docs)
    ? docs.map((d) => (typeof d === 'string' ? d.trim() : '')).filter(Boolean)
    : [];

  return {
    fileReferenceNumber: strField(r, 'fileReferenceNumber', 'file_reference_number'),
    requestSubmittedAt: strField(r, 'requestSubmittedAt', 'request_submitted_at'),
    submittedAgency: strField(r, 'submittedAgency', 'submitted_agency', 'submitted_agency_name'),
    submittedDivisionName: strField(
      r,
      'submittedDivisionName',
      'submitted_division_name',
      'submittedDivision',
      'submitted_division',
      'division_name',
      'divisionName',
    ),
    fileCategory: strField(r, 'fileCategory', 'file_category'),
    subFileCategory: strField(r, 'subFileCategory', 'sub_file_category'),
    applicableAreas: strField(r, 'applicableAreas', 'applicable_areas'),
    description: strField(r, 'description'),
    urgencyLevel: strField(r, 'urgencyLevel', 'urgency_level'),
    deadline: strField(r, 'deadline'),
    urgencyJustification: strField(r, 'urgencyJustification', 'urgency_justification'),
    caseSource: strField(r, 'caseSource', 'case_source'),
    connectedAgdFileReferenceNumber: strField(
      r,
      'connectedAgdFileReferenceNumber',
      'connected_agd_file_reference_number',
    ),
    accusedOrSuspectName: strField(r, 'accusedOrSuspectName', 'accused_or_suspect_name'),
    accusedOrSuspectStatus: strField(r, 'accusedOrSuspectStatus', 'accused_or_suspect_status'),
    suspects: parseCriminalCoverNoteSuspects(r.suspects),
    documents: documentList,
    qr: strField(r, 'qr'),
  };
}

function pickCoverNoteString(primary: string, fallback?: string): string {
  const p = primary.trim();
  if (p) return p;
  return fallback?.trim() ?? '';
}

/** Fill cover-note gaps from wizard profile / step data when API fields are empty. */
export function mergeCriminalCoverNoteResponse(
  api: CriminalCoverNoteResponse,
  fallback?: Partial<CriminalCoverNoteResponse>,
): CriminalCoverNoteResponse {
  if (!fallback) return api;

  return {
    ...api,
    fileReferenceNumber: pickCoverNoteString(api.fileReferenceNumber, fallback.fileReferenceNumber),
    requestSubmittedAt: pickCoverNoteString(api.requestSubmittedAt, fallback.requestSubmittedAt),
    submittedAgency: pickCoverNoteString(api.submittedAgency, fallback.submittedAgency),
    submittedDivisionName: pickCoverNoteString(api.submittedDivisionName, fallback.submittedDivisionName),
    fileCategory: pickCoverNoteString(api.fileCategory, fallback.fileCategory),
    subFileCategory: pickCoverNoteString(api.subFileCategory, fallback.subFileCategory),
    applicableAreas: pickCoverNoteString(api.applicableAreas, fallback.applicableAreas),
    description: pickCoverNoteString(api.description, fallback.description),
    urgencyLevel: pickCoverNoteString(api.urgencyLevel, fallback.urgencyLevel),
    deadline: pickCoverNoteString(api.deadline, fallback.deadline),
    urgencyJustification: pickCoverNoteString(api.urgencyJustification, fallback.urgencyJustification),
    caseSource: pickCoverNoteString(api.caseSource, fallback.caseSource),
    connectedAgdFileReferenceNumber: pickCoverNoteString(
      api.connectedAgdFileReferenceNumber,
      fallback.connectedAgdFileReferenceNumber,
    ),
    accusedOrSuspectName: pickCoverNoteString(api.accusedOrSuspectName, fallback.accusedOrSuspectName),
    accusedOrSuspectStatus: pickCoverNoteString(api.accusedOrSuspectStatus, fallback.accusedOrSuspectStatus),
    suspects: api.suspects.length ? api.suspects : fallback.suspects ?? api.suspects,
    documents: api.documents.length ? api.documents : fallback.documents ?? api.documents,
    qr: pickCoverNoteString(api.qr, fallback.qr),
  };
}

async function enrichCriminalCoverNoteDivision(
  caseRequestId: string,
  coverNote: CriminalCoverNoteResponse,
): Promise<CriminalCoverNoteResponse> {
  if (coverNote.submittedDivisionName.trim() || coverNote.submittedAgency.trim()) {
    return coverNote;
  }

  const view = await getCriminalRequestViewData(caseRequestId);
  if (!view.success || !view.data?.step4) return coverNote;

  const division = view.data.step4.submitted_division_name?.trim() ?? '';
  const agency = view.data.step4.submitted_agency_name?.trim() ?? '';
  if (!division && !agency) return coverNote;

  return {
    ...coverNote,
    submittedDivisionName: division || coverNote.submittedDivisionName,
    submittedAgency: agency || coverNote.submittedAgency,
  };
}

/** CMS-CRC-30 — cover note details after submission */
export async function getCriminalCoverNote(
  caseRequestId: string,
  options?: { fallback?: Partial<CriminalCoverNoteResponse> },
): Promise<ApiResult<CriminalCoverNoteResponse>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<unknown>(`/cover-note/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  const normalized = normalizeCriminalCoverNoteResponse(res.data);
  if (!normalized) return { success: false, message: 'Cover note response is invalid.' };

  let data = mergeCriminalCoverNoteResponse(normalized, options?.fallback);
  data = await enrichCriminalCoverNoteDivision(id, data);
  return { success: true, data };
}

/** CMS-CRC-5 — criminal cover note PDF */
export async function downloadCriminalCoverNotePdf(caseRequestId: string): Promise<ApiResult<Blob>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiPdfRequest(`/cover-note/pdf/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}

/** CMS-CRC-6 — download file from DMS (base64 JSON) for preview / download */
export async function downloadCaseFile(fileName: string): Promise<ApiResult<Blob>> {
  const key = fileName.trim();
  if (!key) return { success: false, message: 'File name is required.' };

  const res = await caseApiRequest<CaseFileDownloadResponse>(
    `/file/download/${encodeURIComponent(key)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  return blobFromCaseFileDownloadResponse(res.data);
}

export type CriminalSubmittedDocumentView = {
  id: string;
  documentTypeId: string;
  documentTypeName: string;
  status: string;
  reason: string;
  /** DMS storage key — used for download/preview API only. */
  fileName: string;
  /** User-facing label shown in the UI. */
  originalFileName: string | null;
  fileSize?: number;
  uploadedAt: string;
};

export type CriminalAdditionalDocumentView = {
  id: string;
  /** DMS storage key — used for download/preview API only. */
  fileName: string;
  /** User-facing label shown in the UI. */
  originalFileName: string | null;
  fileSize?: number;
  uploadedAt: string;
};

export type CriminalDocumentsView = {
  submitted: CriminalSubmittedDocumentView[];
  additional: CriminalAdditionalDocumentView[];
};

function docStrField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function docNumField(raw: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return undefined;
}

/** Reads original_file_name; preserves explicit null from API. */
function docOriginalFileNameField(raw: Record<string, unknown>): string | null {
  const hasSnake = 'original_file_name' in raw;
  const hasCamel = 'originalFileName' in raw;
  if (!hasSnake && !hasCamel) return null;

  const v = hasSnake ? raw.original_file_name : raw.originalFileName;
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') {
    const t = v.trim();
    return t ? t : null;
  }
  return null;
}

function normalizeSubmittedDocument(raw: unknown, index: number): CriminalSubmittedDocumentView {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const { fileName, originalFileName } = normalizeDocumentFileFields(
    docStrField(r, 'file_name', 'fileName'),
    docOriginalFileNameField(r),
  );
  return {
    id: docStrField(r, 'case_request_document_id', 'caseRequestDocumentId') || `submitted-${index}`,
    documentTypeId: docStrField(r, 'document_type_id', 'documentTypeId'),
    documentTypeName: docStrField(r, 'document_type_name', 'documentTypeName') || 'Document',
    status: docStrField(r, 'status'),
    reason: docStrField(r, 'reason'),
    fileName,
    originalFileName: originalFileName || null,
    fileSize: docNumField(r, 'file_size', 'fileSize'),
    uploadedAt: docStrField(r, 'uploaded_at', 'uploadedAt'),
  };
}

function normalizeAdditionalDocument(raw: unknown, index: number): CriminalAdditionalDocumentView {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const { fileName, originalFileName } = normalizeDocumentFileFields(
    docStrField(r, 'file_name', 'fileName'),
    docOriginalFileNameField(r),
  );
  return {
    id: docStrField(r, 'case_request_document_id', 'caseRequestDocumentId') || `additional-${index}`,
    fileName,
    originalFileName: originalFileName || null,
    fileSize: docNumField(r, 'file_size', 'fileSize'),
    uploadedAt: docStrField(r, 'uploaded_at', 'uploadedAt'),
  };
}

function unwrapEnvelopeData(raw: unknown): Record<string, unknown> {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  if (r.data && typeof r.data === 'object' && !Array.isArray(r.data)) {
    return r.data as Record<string, unknown>;
  }
  return r;
}

function normalizeDocumentsResponse(raw: unknown): CriminalDocumentsView {
  const r = unwrapEnvelopeData(raw);
  const submittedRaw = r.submitted_documents ?? r.submittedDocuments;
  const additionalRaw = r.additional_documents ?? r.additionalDocuments;
  const submitted = Array.isArray(submittedRaw)
    ? submittedRaw.map((row, i) => normalizeSubmittedDocument(row, i))
    : [];
  const additional = Array.isArray(additionalRaw)
    ? additionalRaw.map((row, i) => normalizeAdditionalDocument(row, i))
    : [];
  return { submitted, additional };
}

async function blobFromCaseFileDownloadResponse(
  payload: CaseFileDownloadResponse,
): Promise<ApiResult<Blob>> {
  if (payload.success === false) {
    return { success: false, message: payload.message?.trim() || 'Download failed.' };
  }
  const content = payload.file_content?.trim();
  if (!content) {
    return { success: false, message: payload.message?.trim() || 'File content is empty.' };
  }
  try {
    const blob = base64ToBlob(content, payload.content_type ?? 'application/octet-stream');
    if (!blob.size) {
      return { success: false, message: 'File is empty or unavailable.' };
    }
    return { success: true, data: blob };
  } catch {
    return { success: false, message: 'Could not decode file content.' };
  }
}

/** CMS-CRC-7 — Documents tab: submitted + additional documents */
export async function getCaseRequestDocuments(
  caseRequestId: string,
): Promise<ApiResult<CriminalDocumentsView>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<CaseRequestDocumentsResponse>(
    `/criminal/documents/${encodeURIComponent(id)}`,
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeDocumentsResponse(res.data) };
}

/** CMS-CRC-8 — download all case request documents as zip (base64 JSON) */
export async function downloadAllCaseRequestDocuments(
  caseRequestId: string,
): Promise<ApiResult<{ blob: Blob; fileName: string }>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<CaseFileDownloadResponse>(
    `/criminal/documents/${encodeURIComponent(id)}/download-all`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const blobRes = await blobFromCaseFileDownloadResponse(res.data);
  if (!blobRes.success) return blobRes;

  const fileName =
    res.data.file_name?.trim() || `case-documents-${id}.zip`;
  return { success: true, data: { blob: blobRes.data, fileName } };
}

/** CMS-CRC-9 — register additional document (after CMS-CRC-3 upload) */
export async function uploadAdditionalCaseDocument(
  caseRequestId: string,
  fileName: string,
  originalFileName?: string,
): Promise<ApiResult<UploadAdditionalDocumentResponse>> {
  const id = caseRequestId.trim();
  const key = fileName.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };
  if (!key) return { success: false, message: 'File name is required.' };

  const qs = new URLSearchParams({ file_name: key });
  const original = originalFileName?.trim();
  if (original) qs.set('original_file_name', original);
  const res = await caseApiRequest<UploadAdditionalDocumentResponse>(
    `/criminal/documents/${encodeURIComponent(id)}/additional?${qs}`,
    { method: 'POST' },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}

/** CMS-CRC-10 — delete additional document */
export async function deleteAdditionalCaseDocument(
  caseRequestId: string,
  caseRequestDocumentId: string,
): Promise<ApiResult<void>> {
  const id = caseRequestId.trim();
  const docId = caseRequestDocumentId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };
  if (!docId) return { success: false, message: 'Document id is required.' };

  const res = await caseApiRequest<unknown>(
    `/criminal/documents/${encodeURIComponent(id)}/additional/${encodeURIComponent(docId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** CMS-CRC-3 — multipart file upload */
export async function uploadCaseFile(file: File): Promise<ApiResult<UploadCaseFileResponse>> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await caseApiRequest<UploadCaseFileResponse>('/file/upload', {
    method: 'POST',
    body: form,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}
